import { DECISION_SOURCE_DUO_FLOW, DECISION_SOURCE_THREAD_RESOLUTION } from '../constants';

const HOUR_IN_MS = 60 * 60 * 1000;
const DAY_IN_MS = 24 * HOUR_IN_MS;

const user = (id, name) => ({ __typename: 'LocalDecisionUser', id, name, avatarUrl: null });

const options = (nodes) => ({
  __typename: 'LocalWorkItemDecisionOptionConnection',
  nodes: nodes.map(({ id, content, selected = false }) => ({
    __typename: 'LocalWorkItemDecisionOption',
    id,
    content,
    selected,
  })),
});

/**
 * Placeholder decisions for the `workplan_decision_log` flag.
 *
 * There is no backend behind the panel yet, so this stands in for the query result. The records are
 * shaped like the proposed storage model, and cover what the card has to survive: a question with a
 * rationale, a suggested answer accepted without one, and a decision marked from a thread that was
 * never phrased as a question. Timestamps are relative to now rather than fixed, so the rendered
 * "time ago" text stays plausible however long the flag sits behind development.
 *
 * @returns {Array<Object>} Stand-in decisions in the shape of the client query.
 */
/* eslint-disable @gitlab/require-i18n-strings -- Stand-in record content, not UI copy: it would only churn gitlab.pot for strings that get deleted with the stub. */
export const buildStubbedDecisions = () => [
  {
    __typename: 'LocalWorkItemDecision',
    id: 'gid://gitlab/WorkItems::Decision/1',
    title:
      'The updated description mentions SCIM auto-provisioning — should we scope that into this MVP, or handle it as a follow-up?',
    description:
      'GitLab Duo noticed the description was updated to reference SCIM auto-provisioning. Before creating the workplan, it needed a stance on whether SCIM belongs in this MVP or a follow-up work item.',
    resolutionRationale:
      'Provisioning and authentication ship together, so splitting them would leave the MVP with accounts that nothing creates.',
    source: DECISION_SOURCE_DUO_FLOW,
    resolvedAt: new Date(Date.now() - 2 * HOUR_IN_MS).toISOString(),
    noteUrl: '#note_1000001',
    author: user('gid://gitlab/User/1', 'GitLab Duo'),
    resolvedBy: user('gid://gitlab/User/2', 'Avery Patel'),
    options: options([
      { id: 'gid://gitlab/WorkItems::DecisionOption/1', content: 'Handle SCIM as a follow-up' },
      {
        id: 'gid://gitlab/WorkItems::DecisionOption/2',
        content: 'Include SCIM alongside SAML in this MVP',
        selected: true,
      },
    ]),
  },
  {
    __typename: 'LocalWorkItemDecision',
    id: 'gid://gitlab/WorkItems::Decision/2',
    title: 'Is there emergency access if the IdP is unreachable and SSO is enforced?',
    description:
      'GitLab Duo offered this as a suggested answer on the spec. Open question on how admins stay reachable if the IdP is down and SSO is the only sanctioned login path.',
    // Someone accepted a suggested answer without arguing for it, so there is no rationale to show.
    resolutionRationale: null,
    source: DECISION_SOURCE_DUO_FLOW,
    resolvedAt: new Date(Date.now() - DAY_IN_MS).toISOString(),
    noteUrl: '#note_1000002',
    author: user('gid://gitlab/User/1', 'GitLab Duo'),
    resolvedBy: user('gid://gitlab/User/2', 'Avery Patel'),
    options: options([
      {
        id: 'gid://gitlab/WorkItems::DecisionOption/3',
        content: 'No backup — full lockout, contact support to restore access',
        selected: true,
      },
      {
        id: 'gid://gitlab/WorkItems::DecisionOption/4',
        content: 'Admins keep a backup email and password login',
      },
    ]),
  },
  {
    __typename: 'LocalWorkItemDecision',
    id: 'gid://gitlab/WorkItems::Decision/3',
    // Marked from a thread that was never phrased as an open question, so the card has no question
    // line to show. The proposed schema has `title` as not null, which is an open point.
    title: null,
    description:
      'Several IdPs in the field ship non-conformant assertions. The team needed a stance before the validation layer was written.',
    resolutionRationale:
      'Accepting deviations once would make every later assertion a special case, and the team has no capacity to maintain per-vendor branches.',
    source: DECISION_SOURCE_THREAD_RESOLUTION,
    resolvedAt: new Date(Date.now() - 3 * DAY_IN_MS).toISOString(),
    noteUrl: '#note_1000003',
    author: user('gid://gitlab/User/3', 'Parker Osinski'),
    resolvedBy: user('gid://gitlab/User/3', 'Parker Osinski'),
    options: options([
      {
        id: 'gid://gitlab/WorkItems::DecisionOption/5',
        content: 'Strict spec compliance only — reject any non-conformant assertions',
        selected: true,
      },
    ]),
  },
];
/* eslint-enable @gitlab/require-i18n-strings */

// Serves the client-only `workItemDecisions` field. Registered on the issuable Apollo client, next
// to the other work item client resolvers.
// TODO: Drop this file once the `decisionLog` widget is exposed over GraphQL.
// https://gitlab.com/gitlab-org/gitlab/-/work_items/615863
export const decisionLogStubResolvers = {
  Query: {
    workItemDecisions: () => ({
      __typename: 'LocalWorkItemDecisionConnection',
      nodes: buildStubbedDecisions(),
    }),
  },
};
